import { Table } from '@/chassis/ui/components';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';
import { Skeleton } from '@/chassis/ui/components/visual-feedback/Skeleton';

type MessageKey = 'loading' | 'empty';

const defaultMessages: Record<MessageKey, Record<Language, string>> = {
    loading: {
        pt: 'Carregando...',
        en: 'Loading...',
        es: 'Cargando...',
    },
    empty: {
        pt: 'Nenhum registro encontrado.',
        en: 'No records found.',
        es: 'Ningún registro encontrado.',
    },
};

export interface ListTableStateProps {
    loading?: boolean;
    hasData: boolean;
    colSpan: number;
    loadingMessage?: Partial<Record<Language, string>> | string;
    emptyMessage?: Partial<Record<Language, string>> | string;
    /** Optional className override for the TD wrapper */
    className?: string;
}

function resolveMessage(message: Partial<Record<Language, string>> | string | undefined, lang: Language, fallbackKey: MessageKey) {
    if (!message) {
        return defaultMessages[fallbackKey][lang];
    }

    if (typeof message === 'string') {
        return message;
    }

    return message[lang] ?? message.pt ?? defaultMessages[fallbackKey][lang];
}

const baseCellClass = 'py-10 text-center text-slate-500';
// Nº de linhas-fantasma exibidas enquanto carrega (skeleton).
const SKELETON_ROWS = 5;

export default function ListTableState({
    loading = false,
    hasData,
    colSpan,
    loadingMessage,
    emptyMessage,
    className,
}: ListTableStateProps) {
    const lang = useLanguage();

    if (loading) {
        // Design System: skeleton de linhas (mais fluido que spinner + "Carregando...").
        // loadingMessage é mantido na API por compatibilidade, mas não é mais exibido.
        void loadingMessage;
        return (
            <>
                {Array.from({ length: SKELETON_ROWS }).map((_, rowIdx) => (
                    <Table.Tr key={`skeleton-row-${rowIdx}`}>
                        {Array.from({ length: colSpan }).map((_, colIdx) => (
                            <Table.Td key={colIdx} className="py-3">
                                <Skeleton className={`h-4 ${colIdx === 0 ? 'w-3/4 max-w-[220px]' : 'w-1/2 max-w-[140px]'}`} />
                            </Table.Td>
                        ))}
                    </Table.Tr>
                ))}
            </>
        );
    }

    if (hasData) {
        return null;
    }

    return (
        <Table.Tr>
            <Table.Td colSpan={colSpan} className={className ?? baseCellClass}>
                {resolveMessage(emptyMessage, lang, 'empty')}
            </Table.Td>
        </Table.Tr>
    );
}
